import * as React from 'react';
import {
 X, Check
} from 'lucide-react';
import {
 cn
} from 'shared/lib/utils';
import {
 Command, CommandEmpty, CommandGroup, CommandInput, CommandItem
} from 'shared/components/ui/command';
import {
 Popover, PopoverContent, PopoverTrigger
} from 'shared/components/ui/popover';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Button
} from 'shared/components/ui/button';

interface Option {
    label: string;
    value: string;
    color?: string; // Optional color property for the option
}

interface MultiSelectProps {
    options: Option[]; // Array of options with label, value, and optional color
    value: string[]; // Array of selected values
    onChange: (newValue: string[]) => void; // Callback for updating the selected values
    placeholder?: string; // Optional placeholder text for the button
    emptyText?: string; // Optional text for when no items are found
    className?: string; // Optional class name for customization
    displayMode?: 'default' | 'count'; // Mode to display selected items ('count' or 'default')
    type?: 'value' | 'fieldsAccessBy' | 'text' | 'number'| null; // Optional type prop to map labels to values
}

export function MultiSelect({
    options,
    value,
    onChange,
    placeholder = 'Select items...',
    emptyText = 'No items found.',
    className,
    displayMode = 'default',
    type = null,
}: MultiSelectProps) {
    if (type) {
        const updatedValue = value.map((label) => {
            const option = options.find((opt) => opt.label === label);
            return option ? option.value : label; // If label is found, replace it with the value, otherwise keep the original
        });
        value = updatedValue;
    }

    const [open, setOpen] = React.useState(false);

    const handleUnselect = (item: string) => {
        onChange(value.filter((i) => i !== item));
    };

    const handleSelect = (item: string) => {
        if (value.includes(item)) {
            handleUnselect(item);
        } else {
            onChange([...value, item]);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    className={cn(
                        'w-full justify-between',
                        !value.length && 'text-muted-foreground',
                        className
                    )}
                >
                    {value.length ? (
                        displayMode === 'count' ? (
                            <span>{value.length} selected</span>
                        ) : (
                            <div className="flex gap-1 flex-wrap">
                                {value.map((item) => (
                                    <Badge
                                        key={item}
                                        variant="secondary"
                                        className="mr-1"
                                    >
                                        {options.find((option) => option.value === item)?.label || item}
                                        <button
                                            className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleUnselect(item);
                                                }
                                            }}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            onClick={() => handleUnselect(item)}
                                        >
                                            <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )
                    ) : (
                        placeholder
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
                <Command>
                    <CommandInput placeholder={`Search ${placeholder.toLowerCase()}`} />
                    <CommandEmpty>{emptyText}</CommandEmpty>
                    <CommandGroup className="max-h-64 overflow-auto">
                        {options.map((option) => (
                            <CommandItem
                                key={option.value}
                                onSelect={() => handleSelect(option.value)}
                            >
                                <Check
                                    className={cn(
                                        'mr-2 h-4 w-4',
                                        value.includes(option.value)
                                            ? 'opacity-100'
                                            : 'opacity-0'
                                    )}
                                />
                                <span className={cn(
                                    'inline-block px-2 py-1 rounded',
                                    option.color
                                )}>
                                    {option.label}
                                </span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
